import { useCallback, useContext, useState } from "react";

import { OrganisationTypeConstants, UserTypeLabels } from "../../../../../constants";
import { inviteOrganisationToProject } from "../../../../../service/organisation";
import { ServiceError, Status } from "../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../service/ValidationErrorFormatter";
import { useAuth } from "../../../../../useAuth";
import { Toast } from "../../../../../widget";
import { ProjectContext } from "../../../../shared/projects/project/ProjectContext";

interface UseRequestProjectAccessModalReturnData {
  errors: ServiceError[] | undefined;
  isSendRequestLoading: boolean;
  onSendRequest: () => Promise<void>;
}

export const useRequestProjectAccessModal = (
  onClose: () => void,
  onRequestSent?: () => void
): UseRequestProjectAccessModalReturnData => {
  const { currentOrganisationUuid } = useAuth();
  const { publicProjectDetails } = useContext(ProjectContext);
  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isSendRequestLoading, setIsSendRequestLoading] = useState(false);

  const onSendRequest = useCallback(async (): Promise<void> => {
    const projectUuid = publicProjectDetails?.uuid;

    if (!projectUuid) {
      setErrors([
        {
          code: "NO_PROJECT",
          message: "A project must be selected",
        },
      ]);
      return;
    }

    setIsSendRequestLoading(true);
    setErrors(undefined);

    try {
      const inviteResponse = await inviteOrganisationToProject({
        projectUuid,
        type: "Request",
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        targetOrganisationUuid: publicProjectDetails!.responsibleOwnerOrganisationUuid!,
        targetOrganisationEmailOverride: null,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        sourceOrganisationUuid: currentOrganisationUuid!,
        role: UserTypeLabels[OrganisationTypeConstants.ASSET_MANAGER],
      });

      if (inviteResponse.status === Status.Success) {
        Toast.success({ message: "Access request sent" });
        onRequestSent?.();
        onClose();
        return;
      }

      setErrors(inviteResponse.errors);
      if (inviteResponse.errors?.length) {
        Toast.error({ message: getErrorMessageFromCode(inviteResponse.errors[0].code) });
      } else {
        Toast.error({ message: "Access request failed to send" });
      }
    } finally {
      setIsSendRequestLoading(false);
    }
  }, [currentOrganisationUuid, onClose, onRequestSent, publicProjectDetails]);

  return {
    errors,
    isSendRequestLoading,
    onSendRequest,
  };
};
